import { css } from '@emotion/css'
import { faSearch } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { ActionIcon, Autocomplete, useMantineTheme } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { serviceCenterStore } from 'App/ServiceCenter/store/serviceCenterStore'
import { CloseIcon } from 'components/ModalDrawer/CloseIcon/CloseIcon'
import { translate } from 'i18n/i18n'
import { isEmpty } from 'lodash'
import type { FC } from 'react'
import { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { colors } from 'settings/colors'
import { fieldAssetStore } from 'stores/fieldAssetStore/fieldAssetStore'
import { mapSearchStore } from 'stores/mapSearchStore/mapSearchStore'
import { selectedFieldAssetsStore } from 'stores/selectedFieldAssetsStore/selectedFieldAssetsStore'
import { isUserOnlyAFreeRegionalUser } from 'utils/isUserOnlyAFreeRegionalUser'
import { setSelectedFieldAsset } from 'utils/setSelectedFieldAsset/setSelectedFieldAsset'
import { useScreenSize } from 'utils/useScreenSize'
import { getPropertyBounds } from '../../../utils/useMapCenteredOnProperties'
import { getMapOverlaysPadding } from '../CurrentValuesMap/_utils/getMapOverlaysPadding'
import { MapContext } from '../MapContext/MapContext'
import { addPreviousSearch } from './_utils/addPreviousSearch'
import { generatePropertyLabel } from './_utils/generatePropertyLabel'
import { getSearchResults } from './_utils/getSearchResults'
import { AutoCompleteItem } from './AutoCompleteItem/AutoCompleteItem'
import { NarrowScreenSearch } from './NarrowScreenSearch'
import type { TSearchResultsItem } from './types'
import { WideScreenSearch } from './WideScreenSearch'

// Autocomplete has no empty-state prop, so a single disabled option carries the message
const NOTHING_FOUND_VALUE = 'MAP_SEARCH_NOTHING_FOUND'

export const MapSearch: FC = () => {
  const [searchText, setSearchText] = useState('')
  const [opened, { open, close }] = useDisclosure(false)
  const { isWideScreen, screenWidth } = useScreenSize()
  const theme = useMantineTheme()
  const { map } = useContext(MapContext)
  const width = isWideScreen ? 310 : screenWidth
  const height = 45
  const properties = fieldAssetStore.useSelector((s) => s?.properties ?? {})
  const previousSearches = mapSearchStore.useSelector((s) => s.previousSearches)
  const searchRef = useRef<HTMLInputElement>(null)

  const searchResults = useMemo(
    () => getSearchResults({ properties, previousSearches, searchText }),
    [properties, previousSearches, searchText],
  )

  useEffect(() => {
    if (opened && !isWideScreen) {
      setTimeout(() => searchRef?.current?.focus(), 50)
    }
  }, [opened])

  useEffect(() => {
    const unsubscribe = selectedFieldAssetsStore.subscribeToChanges(
      (s) => s.property,
      (propertyId) => {
        const property = propertyId ? fieldAssetStore.getState()?.properties?.[propertyId] : undefined

        setSearchText(property ? generatePropertyLabel(property) : '')
      },
    )

    return () => {
      unsubscribe()
    }
  }, [])

  useEffect(() => {
    if (!searchText) mapSearchStore.setState((s) => ({ ...s, highlightedPropertyIds: {} }))
  }, [searchText])

  const nothingFoundMessage = translate.phrases.banyanApp(
    'Sorry… no properties were found matching that criteria',
  )

  const showNothingFound = !!searchText && !searchResults.length

  const selectSearchResult = (value: string) => {
    const item = searchResults.flatMap((group) => group.items).find((i) => i.value === value)

    if (!item) return

    // blur before the modal unmounts the field: removing a focused field leaves the iOS app's
    // keyboard plugin with the web view still sized for the keyboard
    if (!isWideScreen) searchRef.current?.blur()

    const [propertyId] = item.propertyIds
    const propertyBounds = getPropertyBounds(propertyId)

    if (!isEmpty(propertyBounds)) {
      map?.fitBounds(propertyBounds, getMapOverlaysPadding())
    }

    serviceCenterStore.actions.setPropertyOverviewZoomLevel(map?.getZoom())

    serviceCenterStore.actions.selectProperty(propertyId)

    setSelectedFieldAsset({ property: propertyId })

    mapSearchStore.setState((s) => ({
      ...s,
      previousSearches: addPreviousSearch(s.previousSearches, item),
      highlightedPropertyIds: Object.fromEntries(item.propertyIds.map((id) => [id, true] as const)),
    }))

    // after the selection, which sets the property name: a picked organization, state or country keeps its own
    setSearchText(item.label)

    close()
  }

  const SearchToUse = isWideScreen ? WideScreenSearch : NarrowScreenSearch

  if (isUserOnlyAFreeRegionalUser()) {
    return null
  }

  return (
    <SearchToUse opened={opened} open={open} close={close} setSearchText={setSearchText}>
      <Autocomplete
        ref={searchRef}
        data={showNothingFound ? [{ value: NOTHING_FOUND_VALUE, disabled: true }] : searchResults}
        renderOption={({ option }) => {
          if (option.value === NOTHING_FOUND_VALUE) return <span>{nothingFoundMessage}</span>

          const item = option as TSearchResultsItem

          return <AutoCompleteItem type={item.type} label={item.label} />
        }}
        // on narrow screens the search is a full-screen modal, where a closed dropdown leaves a blank screen
        dropdownOpened={isWideScreen ? undefined : true}
        leftSection={
          isWideScreen ? <FontAwesomeIcon icon={faSearch} size="lg" color={theme.colors.grey[3]} /> : <div />
        }
        leftSectionPointerEvents="none"
        rightSection={
          <ActionIcon
            aria-label={translate.phrases.banyanApp('Clear')}
            onClick={() => {
              setSearchText('')

              searchRef?.current?.focus()
            }}
          >
            {!searchText.length ? null : <CloseIcon size={12} />}
          </ActionIcon>
        }
        onChange={setSearchText}
        // getSearchResults already filtered the options
        filter={({ options }) => options}
        onOptionSubmit={selectSearchResult}
        placeholder={translate.phrases.banyanApp('Search')}
        spellCheck="false"
        classNames={{
          input: css(
            isWideScreen
              ? {
                  width,
                  height,
                }
              : {
                  width,
                  height,
                  'backgroundColor': colors.grey50,
                  'borderWidth': 0,
                  'borderRadius': 0,
                  '&:focus': {
                    border: '0px',
                    boxShadow: '0px 0px 0px white',
                  },
                },
          ),
          dropdown: css(
            !isWideScreen
              ? {
                  width: '100%',
                  overflow: 'auto',
                  borderColor: 'white',
                  borderWidth: 0,
                  height: 'calc(100vh - 65px)',
                }
              : {
                  width,
                  maxHeight: 'min(400px, calc(100vh - 200px))',
                  overflow: 'auto',
                },
          ),
        }}
        value={searchText}
        // the dropdown class above caps the height; Mantine's default 220px list cap would cut results short
        maxDropdownHeight="none"
        enterKeyHint="search"
        autoFocus={!isWideScreen}
      />
    </SearchToUse>
  )
}
